import React, { useState, useRef, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiRequest } from "../utils/api"; 
import { useAuthContext } from "../context/AuthContext"; 
import { useAlert } from '../components/AlertModal';
import { useScrollPersistence } from "../hooks/useScrollPersistence";
import { ScrollRefContext } from "../context/ScrollRefContext";
import { useChatUnread } from "../context/ChatUnreadContext";
import DashboardSidebar from "./DashboardSidebar";
import AccountDropdown from "./AccountDropdown";
import CurrentTime from '../components/CurrentTime';
import WeatherJMA from '../components/WeatherJMA';
import "../styles/DashboardLayout.css";



const DashboardLayout = ({ children }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
    // 日付と曜日を取得
  const dayOfWeek = currentTime.toLocaleString('ja-JP', { weekday: 'long' }); // 曜日
  const formattedDate = currentTime.toLocaleDateString('ja-JP'); // 日付
  const formattedTime = currentTime.toLocaleTimeString('ja-JP'); // 時間
  const [status, setStatus] = useState('BACKUP');
  const navigate = useNavigate();
  const { user: userInfo, setUser, loading } = useAuthContext(); // Contextから取得
  const location = useLocation();

  // レジ画面の場合は最初から閉じておく
  const [isCollapsed, setIsCollapsed] = useState(
    () => location.pathname.toLowerCase() === "/registerpage"
  );

  // レジ画面に移動したときだけ自動で閉じる
  useLayoutEffect(() => {
    if (location.pathname.toLowerCase() === "/registerpage") {
      setIsCollapsed(true);
    }
  }, [location.pathname]);
  
  const { showAlert } = useAlert();
  const dashboardContentRef = useRef(null);
  const { unreadCount } = useChatUnread();

  
  //スクロール保存・復元
  useScrollPersistence(dashboardContentRef, "dashboard-content");

  if (loading) return <div>読み込み中...</div>;
  if (!userInfo) return <div>ログインしていません</div>; // null対策

  const handleLogout = async () => {
    try {
      await apiRequest("accounts/logout/", "POST");
      sessionStorage.clear(); // 必要ならレジIDなども削除
      setUser(null);
      navigate("/login");
    } catch (err) {
      console.error("ログアウト失敗:", err);
    }
  };


  const handleClick = async () => {
    setStatus('Uploading...');
    try {
      const res = await apiRequest('inventory/export_combined_inventory_csv/', 'POST');
      if (res.status !== "success") {
        if (res.errors?.length) {
          const text = res.errors
            .map(err => `ID:${err.inventory_id} / ${err.item_name} / JAN:${err.jan}\n${err.error}`)
            .join('\n\n');

          showAlert(`アップロードに失敗しました\n${res.message}\n\n${text}`);
        } else {
          showAlert(`アップロードに失敗しました: ${res.message || "不明なエラー"}`);
        }
        return;
      }

      setStatus('BACKUP');
      showAlert("すべてのアップロードが完了しました！");
    } catch (e) {
      console.error("ERROR:", e);
      console.log("DETAILS:", e.details);
      setStatus('BACKUP');

      if (e.details?.errors?.length) {
        const text = e.details.errors
          .map(err => `ID:${err.inventory_id} / ${err.item_name} / JAN:${err.jan}\n${err.error}`)
          .join('\n\n');

        showAlert(`アップロードに失敗しました\n${e.details.message || ""}\n\n${text}`);
      } else {
        showAlert(e.details?.message || 'エラーが発生しました');
      }
    }
  };    

  if (loading) return <div>読み込み中...</div>;
  return (
  <ScrollRefContext.Provider value={dashboardContentRef}>
    <div className={`dashboard-layout ${unreadCount > 0 ? "chat-unread" : ""}`}>
      <DashboardSidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      
      <div className={`dashboard-main ${isCollapsed ? "collapsed" : ""}`}>
        <header className="dashboard-header">
          <div className="dashboard_info">
            <CurrentTime setCurrentTime={setCurrentTime} />
            <div className="dashboard-header-date">
              {formattedDate} ({dayOfWeek}) {formattedTime}
            </div>
            <div className="dashboard-header-weather">
              <WeatherJMA />
            </div>
          </div>
          <div className="dashboard_config">
            <div><button onClick={handleClick}>{status}</button></div>
            <div><AccountDropdown userInfo={userInfo} handleLogout={handleLogout} /></div>
          </div>
        </header>

        <main ref={dashboardContentRef} className="dashboard-content">
          {children}
        </main>
      </div>
    </div>
  </ScrollRefContext.Provider>    
  );
};

export default DashboardLayout;
